import { useState, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { Avatar, List } from 'antd';
import './styles.less';
import { getSignatureColor } from 'components/editor/shared/components/SignatureBaseButton';
import Icon from 'components/icon';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { ContentType } from 'utils/getContentType';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useGetCompanyByIdQuery } from 'hooks/useGetCompanyByIdQuery';
import { useGetCrmContactByIdQuery } from 'hooks/useGetCrmContactByIdQuery';
import { useCrmContactsByCompanyId } from 'pages/flex-data/flex-data-links/useCrmContactsByCompanyId';
import { useAccountUsersQuery } from 'hooks/useAccountUsersQuery';
import { useSigneeOptionsQuery } from './useSigneeOptionsQuery';
import { buildSigneeOptions } from './buildSigneeOptions';
import type { SignatureSelectOption } from './types';

export type { SignatureSelectOption } from './types';

import { toCompanyId } from 'services/entities/MigrationEntities';
import { isCrmCompositeId } from 'services/company-and-contacts/mappers';

type Props = {
  value: SignatureSelectOption | null | undefined;
  onChange: (newValue: SignatureSelectOption | null | undefined) => void;
  accountId: number;
  placeholder?: string;
  label?: string;
};

export function SignatureSelect({ value, onChange, accountId, placeholder, label }: Props) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const contentId = useAppSelector(selectContentId);
  const { data: documentData } = useContentMetadata(ContentType.DOCUMENT, contentId);
  const prospectId = toCompanyId(documentData?.prospect?.id);
  const prospectType = documentData?.prospect?.type;
  const isCompositeId = isCrmCompositeId(prospectId);
  const isCrmCompanyProspect = isCompositeId && prospectType === 'COMPANY';
  const isCrmPersonProspect = isCompositeId && prospectType === 'PERSON';
  const {
    data: companyData,
    isFetching: isCompanyFetching,
    refetch: refetchCompany,
  } = useGetCompanyByIdQuery(open && !isCompositeId ? prospectId : null);
  const {
    contacts: crmContacts,
    isLoading: isCrmContactsLoading,
    refetch: refetchCrmContacts,
  } = useCrmContactsByCompanyId({
    companyId: isCrmCompanyProspect && isCrmCompositeId(prospectId) ? prospectId : undefined,
    enabled: open && isCrmCompanyProspect,
  });
  const {
    data: crmContact,
    isFetching: isCrmContactFetching,
    refetch: refetchCrmContact,
  } = useGetCrmContactByIdQuery(isCrmPersonProspect && isCrmCompositeId(prospectId) ? prospectId : null, {
    enabled: open && isCrmPersonProspect,
  });
  const isFetchingSignee = isCrmCompanyProspect ? isCrmContactsLoading : isCrmPersonProspect ? isCrmContactFetching : isCompanyFetching;
  const resolvedCrmContacts = isCrmPersonProspect ? (crmContact ? [crmContact] : []) : crmContacts;
  const { data: signeesData, isFetching: isSigneesFetching } = useSigneeOptionsQuery({
    accountId,
    inputValue,
    enabled: open,
  });
  const { data: usersData, isFetching: isUsersFetching } = useAccountUsersQuery();
  const isLoading = open && (isFetchingSignee || isSigneesFetching || isUsersFetching);

  const options = useMemo(() => {
    const { contacts, users } = buildSigneeOptions({
      isCompositeId,
      companyData,
      crmContacts: resolvedCrmContacts,
      signeesData,
      usersData,
      inputValue,
    });
    return [...contacts, ...users];
  }, [isCompositeId, companyData, resolvedCrmContacts, signeesData, usersData, inputValue]);

  const handleOpen = () => {
    setOpen(true);
    if (prospectId) {
      if (isCrmCompanyProspect) {
        refetchCrmContacts();
      } else if (isCrmPersonProspect) {
        refetchCrmContact();
      } else {
        refetchCompany();
      }
    }
  };

  return (
    <Autocomplete
      autoHighlight
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      inputValue={inputValue}
      onInputChange={(event, value) => setInputValue(value)}
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      options={options}
      loading={isLoading}
      isOptionEqualToValue={(option, value) => option.value === value?.value}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      filterOptions={(x) => x}
      noOptionsText="No signatures found"
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Icon name="IcoSearch" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        return (
          <li key={key} {...restProps}>
            <List.Item.Meta
              className="signature_dropdown__option__meta"
              avatar={<Avatar className={`user_color ${getSignatureColor(option.email)}`}>{option.initials}</Avatar>}
              title={option.label}
              description={option.email}
            />
          </li>
        );
      }}
    />
  );
}
